import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import fs from './FlightSearchForm.module.scss';
import styles from './TripSearchForm.module.scss';
import { SVGMapPin, SVGCalendar, SVGUsers, SVGChevronDown, SVGSearch } from '../assets/PackageSVG';
import { getAirportByCode, type Airport } from '../data/airports';
import { KNOWN_CITIES, searchKnownCities, type KnownCity } from '../utilities/cityDetection';
import { Calendar } from './Calendar';
import { DateRangePickerWidget } from './DateRangePickerWidget';
import { useOrigin } from '../context/OriginContext';
import {
    AirportField,
    FloatingPanel,
    StepRow,
    SwapIcon,
    addDaysISO,
    airportLabel,
    todayISO,
    type FlightSearchCriteria,
    type TripType,
} from './FlightSearchForm';
import type { HotelSearchCriteria } from './HotelSearchForm';

/**
 * Flights and a hotel in one form, for a traveller looking for both.
 *
 * It is the two existing forms folded together: the fields both ask for
 * (dates, travellers) appear once, and the ones that are specific to one
 * search stay separate -- From and To are airports, while the hotel
 * destination is a city or a property name, so the form has all three.
 * FlightSearchForm and HotelSearchForm are unchanged and still used on their own.
 *
 * Submitting hands back one criteria object per search, in exactly the
 * shapes the two forms already produce, so the caller can run
 * hotel-cards and flight-details with the code it already has.
 */
export type TripSearchCriteria = {
    flight: FlightSearchCriteria;
    hotel: HotelSearchCriteria;
};

type TripSearchFormProps = {
    onSearch: (criteria: TripSearchCriteria) => void;
    /** The two data sets the planner returned. Either may be missing. Only
     * read once; later changes don't reset what the traveller is editing. */
    initialFlight?: FlightSearchCriteria;
    initialHotel?: HotelSearchCriteria;
    /** Airport codes, as FlightSearchForm takes them. */
    defaultOrigin?: string;
    defaultDestination?: string;
    /** City or property name, as HotelSearchForm takes it. */
    defaultHotelDestination?: string;
};

type ActiveField = 'origin' | 'destination' | 'hotel' | 'dates' | 'hotelDates' | 'guests' | null;

const MAX_ROOMS = 8;
/** The flight form's limit; the hotel form allows more, but one shared
 * count has to fit both searches. */
const MAX_ADULTS = 9;
const MAX_HOTEL_ADULTS = 16;
const MAX_CHILDREN = 8;

const shortDate = (iso: string) => format(new Date(`${iso}T00:00:00`), 'MMM d');

export function TripSearchForm({
    onSearch,
    initialFlight,
    initialHotel,
    defaultOrigin,
    defaultDestination,
    defaultHotelDestination,
}: TripSearchFormProps) {
    const { origin: detectedOrigin } = useOrigin();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const hotelFieldRef = useRef<HTMLDivElement | null>(null);
    const datesFieldRef = useRef<HTMLButtonElement | null>(null);
    const hotelDatesFieldRef = useRef<HTMLButtonElement | null>(null);
    const guestsBtnRef = useRef<HTMLButtonElement | null>(null);

    /* ── Flight: trip type and airports ───────────────── */
    const [tripType, setTripType] = useState<TripType>(initialFlight?.tripType ?? 'round_trip');

    const initialOrigin = initialFlight ? getAirportByCode(initialFlight.origin) ?? null : null;
    const initialDestination = initialFlight ? getAirportByCode(initialFlight.destination) ?? null : null;
    const [origin, setOriginAirport] = useState<Airport | null>(initialOrigin);
    const [originQuery, setOriginQuery] = useState(initialOrigin ? airportLabel(initialOrigin) : '');
    const [destination, setDestinationAirport] = useState<Airport | null>(initialDestination);
    const [destinationQuery, setDestinationQuery] = useState(initialDestination ? airportLabel(initialDestination) : '');
    // Auto-fill happens at most once per field, tracked outside state so
    // clearing a field afterwards doesn't put the value straight back.
    const originAutoFilledRef = useRef(Boolean(initialOrigin));
    const destinationAutoFilledRef = useRef(Boolean(initialDestination));

    /* ── Hotel destination: a city or a property name ─── */
    const [hotelDestination, setHotelDestination] = useState(initialHotel?.destination ?? defaultHotelDestination ?? '');
    const [latitude, setLatitude] = useState(initialHotel?.latitude);
    const [longitude, setLongitude] = useState(initialHotel?.longitude);
    const [radius] = useState(initialHotel?.radius);
    const hotelTouchedRef = useRef(Boolean(initialHotel?.destination));
    const [highlight, setHighlight] = useState(0);

    /* ── Dates ─────────────────────────────────────────
       One range serves both searches (flight depart/return = hotel
       check-in/check-out) until the two data sets disagree, or the
       traveller asks for different hotel dates; then the hotel keeps its own. */
    const sharedFrom = initialFlight?.departDate ?? initialHotel?.checkIn ?? '';
    const sharedTo = (initialFlight?.tripType === 'round_trip' ? initialFlight.returnDate : undefined)
        ?? initialHotel?.checkOut ?? '';
    const [departDate, setDepartDate] = useState(sharedFrom);
    const [returnDate, setReturnDate] = useState(sharedTo);
    const [sameDates, setSameDates] = useState(
        !(initialHotel && (initialHotel.checkIn !== sharedFrom || initialHotel.checkOut !== sharedTo)),
    );
    const [hotelIn, setHotelIn] = useState(initialHotel?.checkIn ?? '');
    const [hotelOut, setHotelOut] = useState(initialHotel?.checkOut ?? '');

    /* ── Travellers, shared ───────────────────────────── */
    const [rooms, setRooms] = useState(initialHotel?.rooms ?? 1);
    const initialHotelAdults = initialHotel?.adults ?? initialFlight?.travelers.adults ?? 2;
    const initialFlightAdults = initialFlight?.travelers.adults ?? initialHotel?.adults ?? 2;
    const initialHotelChildren = initialHotel?.children ?? initialFlight?.travelers.children ?? [];
    const initialFlightChildren = initialFlight?.travelers.children ?? initialHotel?.children ?? [];
    const [sameTravelers, setSameTravelers] = useState(
        !initialHotel || !initialFlight || (
            initialHotelAdults === initialFlightAdults &&
            JSON.stringify(initialHotelChildren) === JSON.stringify(initialFlightChildren)
        ),
    );
    const [adults, setAdults] = useState(
        Math.min(initialHotelAdults, MAX_ADULTS),
    );
    const [children, setChildren] = useState<{ age: number }[]>(
        initialHotelChildren,
    );
    const [hotelAdults, setHotelAdults] = useState(Math.min(initialHotelAdults, MAX_HOTEL_ADULTS));
    const [flightAdults, setFlightAdults] = useState(Math.min(initialFlightAdults, MAX_ADULTS));
    const [hotelChildren, setHotelChildren] = useState(initialHotelChildren);
    const [flightChildren, setFlightChildren] = useState(initialFlightChildren);

    const [activeField, setActiveField] = useState<ActiveField>(null);

    // "From" defaults to the detected origin, as in FlightSearchForm.
    useEffect(() => {
        if (originAutoFilledRef.current) return;
        const code = defaultOrigin || detectedOrigin?.airport;
        if (!code) return;
        const airport = getAirportByCode(code);
        if (airport) {
            originAutoFilledRef.current = true;
            setOriginAirport(airport);
            setOriginQuery(airportLabel(airport));
        }
    }, [detectedOrigin?.airport, defaultOrigin]);

    useEffect(() => {
        if (destinationAutoFilledRef.current || !defaultDestination) return;
        const airport = getAirportByCode(defaultDestination);
        if (airport) {
            destinationAutoFilledRef.current = true;
            setDestinationAirport(airport);
            setDestinationQuery(airportLabel(airport));
        }
    }, [defaultDestination]);

    useEffect(() => {
        if (hotelTouchedRef.current || !defaultHotelDestination) return;
        hotelTouchedRef.current = true;
        setHotelDestination(defaultHotelDestination);
    }, [defaultHotelDestination]);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Panels are portaled to document.body, so a click inside one
            // must not count as "outside".
            if (rootRef.current?.contains(target)) return;
            if (target.closest?.('[data-floating-panel]')) return;
            setActiveField(null);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveField(null);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    const citySuggestions = useMemo(() => searchKnownCities(hotelDestination, 8), [hotelDestination]);
    useEffect(() => setHighlight(0), [hotelDestination, activeField]);

    const selectCity = (city: KnownCity) => {
        hotelTouchedRef.current = true;
        setHotelDestination(city.name);
        setLatitude(city.lat);
        setLongitude(city.lng);
        setActiveField(null);
    };

    const selectOrigin = (airport: Airport) => {
        originAutoFilledRef.current = true;
        setOriginAirport(airport);
        setOriginQuery(airportLabel(airport));
        setActiveField(null);
    };

    const selectDestination = (airport: Airport) => {
        destinationAutoFilledRef.current = true;
        setDestinationAirport(airport);
        setDestinationQuery(airportLabel(airport));
        setActiveField(null);
        // The hotel is almost always in the city being flown to, so offer it
        // -- only while the traveller hasn't chosen a hotel destination themselves.
        if (!hotelTouchedRef.current) {
            const city = KNOWN_CITIES.find((c) => c.name.toLowerCase() === airport.city.toLowerCase());
            setHotelDestination(airport.city);
            setLatitude(city?.lat);
            setLongitude(city?.lng);
        }
    };

    const swap = () => {
        originAutoFilledRef.current = true;
        destinationAutoFilledRef.current = true;
        const nextOrigin = destination;
        const nextDestination = origin;
        setOriginAirport(nextOrigin);
        setOriginQuery(nextOrigin ? airportLabel(nextOrigin) : '');
        setDestinationAirport(nextDestination);
        setDestinationQuery(nextDestination ? airportLabel(nextDestination) : '');
    };

    const setChildCount = (count: number) => {
        setChildren((current) => Array.from({ length: count }, (_, i) => current[i] ?? { age: 8 }));
    };

    const setGroupChildCount = (group: 'hotel' | 'flight', count: number) => {
        const setter = group === 'hotel' ? setHotelChildren : setFlightChildren;
        setter((current) => Array.from({ length: count }, (_, i) => current[i] ?? { age: 8 }));
    };

    const renderChildAgeRows = (group: 'hotel' | 'flight', groupChildren: { age: number }[]) =>
        groupChildren.map((child, index) => (
            <div key={`${group}-child-${index}`} className={fs.childAgeRow}>
                <span className={fs.childAgeLabel}>{group === 'hotel' ? 'Hotel' : 'Flight'} child {index + 1} age</span>
                <select
                    className={fs.childAgeSelect}
                    value={child.age}
                    onChange={(e) => {
                        const setter = group === 'hotel' ? setHotelChildren : setFlightChildren;
                        setter((current) => current.map((c, i) => (i === index ? { age: Number(e.target.value) } : c)));
                    }}
                >
                    {Array.from({ length: 18 }, (_, age) => (
                        <option key={age} value={age}>{age === 0 ? '< 1 year' : `${age} year`}</option>
                    ))}
                </select>
            </div>
        ));

    const useSeparateTravelers = () => {
        setHotelAdults(adults);
        setFlightAdults(adults);
        setHotelChildren(children);
        setFlightChildren(children);
        setSameTravelers(false);
    };

    const useSharedTravelers = () => {
        setAdults(hotelAdults);
        setChildren(hotelChildren);
        setSameTravelers(true);
    };

    const useDifferentHotelDates = () => {
        // Start the hotel's own dates from what is shown now.
        setHotelIn(departDate);
        setHotelOut(returnDate);
        setSameDates(false);
    };

    const minDate = todayISO();
    const maxDate = addDaysISO(minDate, 365);

    // What each search will actually use.
    const checkIn = sameDates ? departDate : hotelIn;
    const checkOut = sameDates ? returnDate : hotelOut;
    const flightReturn = tripType === 'round_trip' ? returnDate : '';

    const datesLabel = useMemo(() => {
        if (!departDate) return 'Select dates';
        const from = shortDate(departDate);
        if (!sameDates && tripType === 'one_way') return `Depart ${from}`;
        if (!returnDate) return `${from} – Select ${sameDates ? 'check-out' : 'return'}`;
        return `${from} – ${shortDate(returnDate)}`;
    }, [departDate, returnDate, sameDates, tripType]);

    const hotelDatesLabel = useMemo(() => {
        if (!hotelIn) return 'Add dates';
        if (!hotelOut) return `${shortDate(hotelIn)} – Select check-out`;
        return `${shortDate(hotelIn)} – ${shortDate(hotelOut)}`;
    }, [hotelIn, hotelOut]);

    const guestsLabel = useMemo(() => {
        if (!sameTravelers) {
            return `Hotel: ${hotelAdults} adult${hotelAdults > 1 ? 's' : ''}, ${hotelChildren.length} child${hotelChildren.length === 1 ? '' : 'ren'} · Flight: ${flightAdults} adult${flightAdults > 1 ? 's' : ''}, ${flightChildren.length} child${flightChildren.length === 1 ? '' : 'ren'}`;
        }
        const parts = [`${rooms} room${rooms > 1 ? 's' : ''}`, `${adults} adult${adults > 1 ? 's' : ''}`];
        if (children.length > 0) parts.push(`${children.length} child${children.length > 1 ? 'ren' : ''}`);
        return parts.join(', ');
    }, [sameTravelers, rooms, adults, children.length, hotelAdults, hotelChildren.length, flightAdults, flightChildren.length]);

    const sameAirport = Boolean(origin && destination && origin.code === destination.code);
    const canSearch = Boolean(
        origin && destination && !sameAirport && departDate
        && (tripType === 'one_way' || flightReturn)
        && hotelDestination.trim() && checkIn && checkOut,
    );

    /* The button stays at full strength, so an early click takes the
       traveller to the first field that still needs an answer. */
    const openFirstMissing = () => {
        if (!origin) document.getElementById('trip-origin')?.focus();
        else if (!destination || sameAirport) document.getElementById('trip-destination')?.focus();
        else if (!hotelDestination.trim()) document.getElementById('trip-hotel')?.focus();
        else if (!departDate || (sameDates && !returnDate) || (!sameDates && tripType === 'round_trip' && !returnDate)) setActiveField('dates');
        else setActiveField('hotelDates');
    };

    const handleSearch = () => {
        if (!canSearch || !origin || !destination) {
            openFirstMissing();
            return;
        }
        onSearch({
            flight: {
                tripType,
                origin: origin.code,
                destination: destination.code,
                departDate,
                returnDate: tripType === 'round_trip' ? returnDate : undefined,
                travelers: {
                    adults: sameTravelers ? adults : flightAdults,
                    children: sameTravelers ? children : flightChildren,
                },
            },
            hotel: {
                destination: hotelDestination.trim(),
                latitude,
                longitude,
                radius,
                checkIn,
                checkOut,
                rooms,
                adults: sameTravelers ? adults : hotelAdults,
                children: sameTravelers ? children : hotelChildren,
            },
        });
    };

    const datesPickerLabels = sameDates
        ? { from: 'Depart / Check-in', to: tripType === 'round_trip' ? 'Return / Check-out' : 'Check-out' }
        : { from: 'Depart', to: 'Return' };

    return (
        <div ref={rootRef} className={`${fs.root} ${styles.root}`}>
            <div className={styles.body}>
                <div className={fs.topRow}>
                    <div className={fs.tripTypeToggle} role="radiogroup" aria-label="Trip type">
                        <button
                            type="button"
                            role="radio"
                            aria-checked={tripType === 'round_trip'}
                            className={`${fs.tripTypeBtn} ${tripType === 'round_trip' ? fs.tripTypeBtnActive : ''}`}
                            onClick={() => setTripType('round_trip')}
                        >
                            Round trip
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={tripType === 'one_way'}
                            className={`${fs.tripTypeBtn} ${tripType === 'one_way' ? fs.tripTypeBtnActive : ''}`}
                            onClick={() => setTripType('one_way')}
                        >
                            One way
                        </button>
                    </div>

                    <div className={fs.popoverAnchor}>
                        <button
                            ref={guestsBtnRef}
                            type="button"
                            className={fs.pillButton}
                            aria-expanded={activeField === 'guests'}
                            onClick={() => setActiveField((f) => (f === 'guests' ? null : 'guests'))}
                        >
                            <SVGUsers className={fs.pillIcon} aria-hidden="true" />
                            {guestsLabel}
                            <SVGChevronDown className={fs.pillChevron} aria-hidden="true" />
                        </button>

                        {activeField === 'guests' && (
                            <FloatingPanel anchorRef={guestsBtnRef} align="right">
                                <div className={fs.popoverPanel}>
                                        {sameTravelers ? (
                                            <>
                                                <StepRow label="Rooms" sublabel="Hotel" value={rooms} min={1} max={MAX_ROOMS} onChange={setRooms} />
                                                <StepRow label="Adults" value={adults} min={1} max={MAX_ADULTS} onChange={setAdults} />
                                                <StepRow label="Children" sublabel="Ages 0–17" value={children.length} min={0} max={MAX_CHILDREN} onChange={setChildCount} />
                                                {children.map((child, index) => (
                                                    <div key={index} className={fs.childAgeRow}>
                                                        <span className={fs.childAgeLabel}>Child {index + 1} age</span>
                                                        <select
                                                            className={fs.childAgeSelect}
                                                            value={child.age}
                                                            onChange={(e) => setChildren((current) => current.map((c, i) => (i === index ? { age: Number(e.target.value) } : c)))}
                                                        >
                                                            {Array.from({ length: 18 }, (_, age) => (
                                                                <option key={age} value={age}>{age === 0 ? '< 1 year' : `${age} year`}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <span className={fs.stepLabel}>Hotel guests</span>
                                                <StepRow label="Rooms" value={rooms} min={1} max={MAX_ROOMS} onChange={setRooms} />
                                                <StepRow label="Adults" value={hotelAdults} min={1} max={MAX_HOTEL_ADULTS} onChange={setHotelAdults} />
                                                <StepRow label="Children" sublabel="Ages 0–17" value={hotelChildren.length} min={0} max={MAX_CHILDREN} onChange={(count) => setGroupChildCount('hotel', count)} />
                                                {renderChildAgeRows('hotel', hotelChildren)}
                                                <span className={fs.stepLabel}>Flight passengers</span>
                                                <StepRow label="Adults" value={flightAdults} min={1} max={MAX_ADULTS} onChange={setFlightAdults} />
                                                <StepRow label="Children" sublabel="Ages 0–17" value={flightChildren.length} min={0} max={MAX_CHILDREN} onChange={(count) => setGroupChildCount('flight', count)} />
                                                {renderChildAgeRows('flight', flightChildren)}
                                            </>
                                        )}
                                    <button type="button" className={fs.popoverDoneBtn} onClick={() => setActiveField(null)}>
                                        Done
                                    </button>
                                </div>
                            </FloatingPanel>
                        )}
                    </div>
                </div>

                {/* Where: the flight's two airports, and the hotel's city or property. */}
                <div className={styles.locationRow}>
                    <div className={fs.airportPair}>
                        <AirportField
                            id="trip-origin"
                            label="From"
                            placeholder="Where from?"
                            query={originQuery}
                            onQueryChange={(v) => { originAutoFilledRef.current = true; setOriginQuery(v); setOriginAirport(null); }}
                            selected={origin}
                            onSelect={selectOrigin}
                            isOpen={activeField === 'origin'}
                            onOpen={() => setActiveField('origin')}
                            onClose={() => setActiveField(null)}
                        />
                        <button type="button" className={fs.swapButton} onClick={swap} aria-label="Swap origin and destination">
                            <SwapIcon className={fs.swapIcon} />
                        </button>
                        <AirportField
                            id="trip-destination"
                            label="To"
                            placeholder="Where to?"
                            query={destinationQuery}
                            onQueryChange={(v) => { destinationAutoFilledRef.current = true; setDestinationQuery(v); setDestinationAirport(null); }}
                            selected={destination}
                            onSelect={selectDestination}
                            isOpen={activeField === 'destination'}
                            onOpen={() => setActiveField('destination')}
                            onClose={() => setActiveField(null)}
                        />
                    </div>

                    <div className={fs.fieldWrap} ref={hotelFieldRef}>
                        <label className={fs.fieldLabel} htmlFor="trip-hotel">Hotel destination or name</label>
                        <div className={fs.airportInputWrap}>
                            <SVGMapPin className={fs.fieldIcon} aria-hidden="true" />
                            <input
                                id="trip-hotel"
                                className={fs.airportInput}
                                type="text"
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={activeField === 'hotel'}
                                aria-controls="trip-hotel-listbox"
                                placeholder="City or hotel"
                                value={hotelDestination}
                                onFocus={() => setActiveField('hotel')}
                                onChange={(e) => {
                                    hotelTouchedRef.current = true;
                                    setHotelDestination(e.target.value);
                                    setLatitude(undefined);
                                    setLongitude(undefined);
                                    setActiveField('hotel');
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setActiveField('hotel');
                                        setHighlight((i) => Math.min(i + 1, citySuggestions.length - 1));
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setHighlight((i) => Math.max(i - 1, 0));
                                    } else if (e.key === 'Enter') {
                                        const pick = citySuggestions[highlight];
                                        if (activeField === 'hotel' && pick) {
                                            e.preventDefault();
                                            selectCity(pick);
                                        }
                                    } else if (e.key === 'Escape') {
                                        setActiveField(null);
                                    }
                                }}
                            />
                        </div>

                        {activeField === 'hotel' && citySuggestions.length > 0 && (
                            <FloatingPanel anchorRef={hotelFieldRef} align="left" matchWidth>
                                <ul id="trip-hotel-listbox" role="listbox" className={fs.airportDropdown}>
                                    {citySuggestions.map((city, idx) => (
                                        <li
                                            key={city.name}
                                            role="option"
                                            aria-selected={hotelDestination === city.name}
                                            className={`${fs.airportOption} ${idx === highlight ? fs.airportOptionActive : ''}`}
                                            onMouseEnter={() => setHighlight(idx)}
                                            onMouseDown={(e) => { e.preventDefault(); selectCity(city); }}
                                        >
                                            <SVGMapPin className={fs.pillIcon} aria-hidden="true" />
                                            <span className={fs.airportInfo}>
                                                <span className={fs.airportCity}>{city.name}</span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </FloatingPanel>
                        )}
                    </div>
                </div>

                {/* When: one range for both searches, unless the hotel needs its own. */}
                <div className={sameDates ? styles.dateRow : `${styles.dateRow} ${styles.dateRowSplit}`}>
                    <div className={fs.popoverAnchor}>
                        <button
                            ref={datesFieldRef}
                            type="button"
                            className={`${fs.fieldWrap} ${fs.dateField}`}
                            aria-expanded={activeField === 'dates'}
                            onClick={() => setActiveField((f) => (f === 'dates' ? null : 'dates'))}
                        >
                            <span className={fs.fieldLabel}>{sameDates ? 'Dates (flight and hotel)' : 'Flight dates'}</span>
                            <span className={`${fs.dateValue} ${departDate ? '' : fs.dateValueEmpty}`}>
                                <SVGCalendar className={fs.fieldIcon} aria-hidden="true" />
                                {datesLabel}
                            </span>
                        </button>

                        {activeField === 'dates' && (sameDates || tripType === 'round_trip') && (
                            <FloatingPanel anchorRef={datesFieldRef} align="left">
                                <div className={`${fs.popoverPanel} ${fs.datePopoverPanel}`}>
                                    <DateRangePickerWidget
                                        labels={datesPickerLabels}
                                        payload={{
                                            min_date: minDate,
                                            max_date: maxDate,
                                            default_from: departDate || minDate,
                                            default_to: returnDate || addDaysISO(departDate || minDate, sameDates ? 4 : 7),
                                            submitted_value: departDate && returnDate
                                                ? { from: departDate, to: returnDate, nights: 0 }
                                                : undefined,
                                        }}
                                        onSubmit={(value) => {
                                            setDepartDate(value.from);
                                            setReturnDate(value.to);
                                            setActiveField(null);
                                        }}
                                    />
                                </div>
                            </FloatingPanel>
                        )}

                        {activeField === 'dates' && !sameDates && tripType === 'one_way' && (
                            <FloatingPanel anchorRef={datesFieldRef} align="left">
                                <div className={`${fs.popoverPanel} ${fs.singleDatePanel}`}>
                                    <h3 className={fs.singleDateTitle}>Depart</h3>
                                    <Calendar
                                        start={departDate || undefined}
                                        min={minDate}
                                        max={maxDate}
                                        onSelect={(iso) => {
                                            setDepartDate(iso);
                                            setActiveField(null);
                                        }}
                                    />
                                </div>
                            </FloatingPanel>
                        )}
                    </div>

                    {!sameDates && (
                        <div className={fs.popoverAnchor}>
                            <button
                                ref={hotelDatesFieldRef}
                                type="button"
                                className={`${fs.fieldWrap} ${fs.dateField}`}
                                aria-expanded={activeField === 'hotelDates'}
                                onClick={() => setActiveField((f) => (f === 'hotelDates' ? null : 'hotelDates'))}
                            >
                                <span className={fs.fieldLabel}>Hotel dates</span>
                                <span className={`${fs.dateValue} ${hotelIn ? '' : fs.dateValueEmpty}`}>
                                    <SVGCalendar className={fs.fieldIcon} aria-hidden="true" />
                                    {hotelDatesLabel}
                                </span>
                            </button>

                            {activeField === 'hotelDates' && (
                                <FloatingPanel anchorRef={hotelDatesFieldRef} align="left">
                                    <div className={`${fs.popoverPanel} ${fs.datePopoverPanel}`}>
                                        <DateRangePickerWidget
                                            labels={{ from: 'Check-in', to: 'Check-out' }}
                                            payload={{
                                                min_date: minDate,
                                                max_date: addDaysISO(minDate, 500),
                                                default_from: hotelIn || departDate || minDate,
                                                default_to: hotelOut || addDaysISO(hotelIn || departDate || minDate, 2),
                                                submitted_value: hotelIn && hotelOut
                                                    ? { from: hotelIn, to: hotelOut, nights: 0 }
                                                    : undefined,
                                            }}
                                            onSubmit={(value) => {
                                                setHotelIn(value.from);
                                                setHotelOut(value.to);
                                                setActiveField(null);
                                            }}
                                        />
                                    </div>
                                </FloatingPanel>
                            )}
                        </div>
                    )}

                    <button type="button" className={fs.searchButton} aria-disabled={!canSearch} onClick={handleSearch}>
                        <SVGSearch className={fs.searchIcon} aria-hidden="true" />
                        Search hotels &amp; flights
                    </button>
                </div>

                <button
                    type="button"
                    className={styles.datesToggle}
                    onClick={() => (sameDates ? useDifferentHotelDates() : setSameDates(true))}
                >
                    {sameDates ? 'Different dates for the hotel' : 'Use the same dates for the hotel'}
                </button>

                <button
                    type="button"
                    className={styles.datesToggle}
                    onClick={() => (sameTravelers ? useSeparateTravelers() : useSharedTravelers())}
                >
                    {sameTravelers ? 'Different travelers for hotel and flight' : 'Use the same travelers for both'}
                </button>

                {sameAirport && (
                    <p className={fs.inlineWarning}>Origin and destination can't be the same airport.</p>
                )}
            </div>
        </div>
    );
}
